<template>
  <section class="w-full pt-4">
    <ScrollPanel
      style="width: 100%; height: 450px"
      class="bg-red-4004"
      :pt="{ bary: 'hover:bg-sea-serpent rounded-md bg-gray-300 opacity-100' }"
    >
      <div
        class="edu-preview mt-4"
        v-if="eduPreview.institution || eduPreview.degree || eduPreview.period"
      >
        <pre><code>{{ eduPreview.institution }}
          {{ eduPreview.degree }}
          {{ eduPreview.period }}</code></pre>
      </div>

      <form
        @submit.prevent="JSON.stringify(props.graduationToUpdate) != '{}'
            ? updateGraduation()
            : addNewGraduation()"
        class="grid grid-cols-12 gap-3 px-4 text-base font-normal"
      >
        <span class="col-span-12 md:col-span-6">
          <label class="required-sign">{{ translate('graduation', 'ucfirst') }}</label>
          <CustomInputText
            name="eddegree"
            :placeholder="'Ex: ' + translate('software engineering', 'ucfirst')"
            class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-6">
          <label class="required-sign">{{ translate('institution name', 'ucfirst') }}</label>
          <CustomInputText
            name="edinstitution"
            placeholder="Ex: Harvard"
            class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-6">
          <label class="required-sign">{{ translate('field of study', 'ucfirst') }}</label>
          <CustomDropdown
            name="edfield_of_study"
            :options="fieldsStudy"
            optionLabel="name"
            optionValue="area_of_study_id"
            filter
            :loading="fetchFieldsStudy"
            :placeholder="'Ex: ' + translate('engineering', 'ucfirst')"
            class="w-full px-3 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-6">
          <label class="required-sign">{{ translate('degree type', 'ucfirst') }}</label>
          <CustomDropdown
            name="degree_type"
            :options="degreeTypes"
            optionLabel="name"
            optionValue="degree_type_id"
            :loading="fetchDegreeTypes"
            :placeholder="'Ex: ' + translate('higher education', 'ucfirst')"
            class="w-full px-3 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-6">
          <label class="required-sign">{{ translate('start date', 'ucfirst') }}</label>
          <CustomCalendarDate
            name="edstart_date"
            :maxDate="maxDate.start_date"
            format="yy/mm/dd"
            class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-6">
          <label>{{ translate('end date', 'ucfirst') }}</label>
          <CustomCalendarDate
            name="edend_date"
            :maxDate="maxDate.end_date"
            format="yy/mm/dd"
            class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron"
          />
        </span>

        <span class="col-span-12 md:col-span-12">
          <label>{{ translate('description', 'ucfirst') }}</label>
          <CustomTextArea
            name="eddescription"
            class="w-full min-h-28 max-h-36 px-3 py-2 mt-2.5 rounded-md border border-iron"
          />
        </span>

        <span class="col-span-12 pt-3">
          <Button
            v-if="Object.keys(props.graduationToUpdate).length > 0"
            @click="showConfirmDialog = true"
            type="button"
            :label="translate('delete', 'ucfirst')"
            class="text-white px-5 py-2 float-left rounded-full bg-red-600"
          />
          <Button
            type="submit"
            :label="
              Object.keys(props.graduationToUpdate).length > 0
                ? translate('update', 'ucfirst')
                : translate('save', 'ucfirst')
            "
            class="text-white px-5 py-2 float-right rounded-full bg-sea-serpent"
          />
        </span>
      </form>
    </ScrollPanel>

    <CustomConfirmDialog
      :show-confirm="showConfirmDialog"
      @confirmResponse="confirmDialogResponse"
    />
    <LoadingDialog :visible="isLoading" />
  </section>
</template>

<script setup>
import { ref, onBeforeMount, onMounted, computed } from "vue";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import ScrollPanel from "primevue/scrollpanel";
import { showToast } from "@/composables/toast";
import { translate } from "@/composables/translate";
import requestHandler from "@/composables/requestHandler";
import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
import CustomInputText from "@/components/Base/CustomInputText.vue";
import CustomTextArea from "@/components/Base/CustomTextArea.vue";
import CustomCalendarDate from "@/components/Base/CustomCalendarDate.vue";
import CustomDropdown from "@/components/Base/CustomDropdown.vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, string, date, number } from "yup";
import CustomConfirmDialog from "@/components/Utils/CustomConfirmDialog.vue";

/* ===================== Helpers de data/locale ===================== */
const BROWSER_LOCALE = navigator.language || "pt-BR";
const CURRENTLY_BY_LANG = { pt: "ATUALMENTE", en: "CURRENTLY", fr: "ACTUELLEMENT" };

const baseLang = (loc = BROWSER_LOCALE) => String(loc).toLowerCase().split("-")[0];

const toISODate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d) ? null : d.toISOString().slice(0, 10); // YYYY-MM-DD
};

const formatMonthYearISO = (iso, loc = BROWSER_LOCALE) => {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  let out = new Intl.DateTimeFormat(loc, { year: "numeric", month: "long" }).format(date);
  if (baseLang(loc) === "pt") out = out.replace(" de ", " ");
  return out.toLocaleUpperCase(loc);
};

const formatEducationPeriod = (startISO, endISO, loc = BROWSER_LOCALE) => {
  const start = formatMonthYearISO(startISO, loc);
  const end = endISO ? formatMonthYearISO(endISO, loc) : CURRENTLY_BY_LANG[baseLang(loc)] || "CURRENTLY";
  return `${start} — ${end}`;
};
/* ================================================================= */

let toast = useToast();
let isLoading = ref(false);
let fetchFieldsStudy = ref(false);
let fetchDegreeTypes = ref(false);
let fieldsStudy = ref([]);
let degreeTypes = ref([]);
let maxDate = ref({
  start_date: new Date(),
  end_date: new Date(),
});
let showConfirmDialog = ref(false);

const props = defineProps({
  graduationToUpdate: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  curriculumId: {
    type: Number,
    required: false,
  },
});

const emit = defineEmits(["closeModal", "educationUpdated"]);

/* =============== Validação formulário (vee-validate) =============== */
const { handleSubmit, setValues, values } = useForm({
  validationSchema: toTypedSchema(
    object().shape({
      eddegree: string()
        .required(translate("this field is required", "ucfirst"))
        .min(2, translate("this field must be at least 2 characters", "ucfirst"))
        .max(255, translate("this field must be at most 255 characters", "ucfirst"))
        .ensure()
        .trim()
        .default(""),
      edinstitution: string()
        .required(translate("this field is required", "ucfirst"))
        .min(2, translate("this field must be at least 2 characters", "ucfirst"))
        .max(255, translate("this field must be at most 255 characters", "ucfirst"))
        .ensure()
        .trim()
        .default(""),
      edfield_of_study: number().required(translate("this field is required", "ucfirst")),
      degree_type: number().required(translate("this field is required", "ucfirst")),
      eddescription: string()
        .max(255, translate("this field must be at most 255 characters", "ucfirst"))
        .ensure()
        .trim()
        .default(""),
      edstart_date: date().required(translate("this field is required", "ucfirst")),
      edend_date: date().nullable(),
    })
  ),
});

/* =============== Preview computed (limpa o template) =============== */
const eduPreview = computed(() => {
  const v = values;
  const institution = (v?.edinstitution || props.graduationToUpdate?.institution || "")
    .toString()
    .trim()
    .toUpperCase();

  const degree = (v?.eddegree || props.graduationToUpdate?.degree || "")
    .toString()
    .trim()
    .toUpperCase();

  const startISO = toISODate(v?.edstart_date || props.graduationToUpdate?.start_date?.slice?.(0, 10));
  const endISO = toISODate(v?.edend_date || props.graduationToUpdate?.end_date?.slice?.(0, 10)) || null;

  return {
    institution,
    degree,
    period: startISO ? formatEducationPeriod(startISO, endISO) : "",
  };
});

const addNewGraduation = handleSubmit(async (v) => {
  const formatedValues = {
    eddegree: v.eddegree,
    edfield_of_study: v.edfield_of_study,
    edinstitution: v.edinstitution,
    degree_type: v.degree_type,
    edstart_date: toISODate(v.edstart_date),
    edend_date: v.edend_date ? toISODate(v.edend_date) : null,
    eddescription: v.eddescription,
  };

  if (v) {
    isLoading.value = true;
    const response = await requestHandler.executeRequest(
      "/api/curriculum/education/create",
      formatedValues,
      "post"
    );
    isLoading.value = false;

    if (!response.success) {
      showToast(toast, translate("there was an error adding your education", "ucfirst"), "error");
      return;
    }

    showToast(toast, translate("added education", "ucfirst"));
    emit("closeModal", false);
    emit("educationUpdated", true);
  }
});

const updateGraduation = handleSubmit(async (v) => {
  const formatedValues = {
    education_id: props.graduationToUpdate.id,
    eddegree: v.eddegree,
    edfield_of_study: v.edfield_of_study,
    edinstitution: v.edinstitution,
    degree_type: v.degree_type,
    edstart_date: toISODate(v.edstart_date),
    eddescription: v.eddescription,
    edend_date: v.edend_date ? toISODate(v.edend_date) : null,
  };

  if (v) {
    isLoading.value = true;
    const response = await requestHandler.executeRequest(
      "api/curriculum/education/update",
      formatedValues,
      "put"
    );
    isLoading.value = false;

    if (!response.success) {
      showToast(toast, translate("there was an error updating your education", "ucfirst"), "warn");
      return;
    }
    showToast(toast, translate("your education was updated", "ucfirst"));
    emit("closeModal", false);
    emit("educationUpdated", true);
  }
});

const deleteEducation = handleSubmit(async (v) => {
  const deleteBody = {
    item_type: "education",
    item_id: props.graduationToUpdate.id,
    curriculum_id: props.curriculumId,
  };

  if (v) {
    isLoading.value = true;
    const response = await requestHandler.executeRequest(
      "/api/curriculum/item/delete",
      deleteBody,
      "delete"
    );
    isLoading.value = false;

    if (!response.success) {
      showToast(
        toast,
        translate("there was an error deleting your education", "ucfirst"),
        "warn"
      );
      return;
    }
    showToast(toast, translate("your education was deleted", "ucfirst"));
    emit("closeModal", false);
    emit("educationUpdated", true);
  }
});

function confirmDialogResponse(event) {
  showConfirmDialog.value = event.close;
  if (event.accepted) deleteEducation();
}

/* ===================== Combos / Datas ===================== */
async function getDegreeTypes() {
  fetchDegreeTypes.value = true;
  const response = await requestHandler.executeRequest(
    "api/curriculum/education/degree-types",
    {},
    "get"
  );
  if (response.success) degreeTypes.value = response.data;
  fetchDegreeTypes.value = false;
}

async function getFieldsStudy() {
  fetchFieldsStudy.value = true;
  const response = await requestHandler.executeRequest(
    "api/curriculum/education/areas-of-study",
    {},
    "get"
  );
  if (response.success) fieldsStudy.value = response.data;
  fetchFieldsStudy.value = false;
}

function defineMaxDate() {
  const currentDate = new Date();
  const currentDay = currentDate.getDate();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  maxDate.value.start_date.setMonth(currentMonth - 1);
  maxDate.value.start_date.setFullYear(currentYear);
  maxDate.value.end_date.setDate(currentDay);
  maxDate.value.end_date.setMonth(currentMonth);
  maxDate.value.end_date.setFullYear(currentYear);
}

onBeforeMount(() => {
  defineMaxDate();
  getFieldsStudy();
  getDegreeTypes();
});

onMounted(() => {
  if (Object.keys(props.graduationToUpdate).length > 0) {
    setValues({
      eddegree: props.graduationToUpdate.degree,
      edfield_of_study: props.graduationToUpdate.field_of_study,
      edinstitution: props.graduationToUpdate.institution,
      degree_type: props.graduationToUpdate.degree_type,
      edstart_date: props.graduationToUpdate.start_date.slice(0, 10),
      edend_date: props.graduationToUpdate.end_date.slice(0, 10),
      eddescription: props.graduationToUpdate.description,
    });
  }
});
</script>

<style scoped>
.edu-preview pre {
  background: #2f3341;
  color: #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
}
.edu-preview code {
  display: block;
}
</style>
